import Vue from 'vue';
import { bootstrap, event as gtagEvent } from 'vue-gtag';
import router from '@/router';
import store from '@/store';
import AppConfig from '@/config';
import GaEvent from '@/helpers/ga/events/GaEvent';
import ContentItemDetailsViewedGaEvent from '@/helpers/ga/events/ContentItemDetailsViewedGaEvent';

/**
 * Bootstraps gtag.js after the user grants analytics consent (e.g., via the
 * cookie policy modal). Re-assigns `Vue.prototype.$gtagReady` to a fresh promise that
 * resolves once gtag.js has finished loading, so later `sendGaEvent`
 * calls wait on this new promise instead of the one set at app boot.
 */
export function enableAnalyticsAfterConsent() {
  Vue.prototype.$gtagReady = bootstrap();
}

/**
 * Sends a GA event once gtag.js is ready (bootstrapped and consent granted).
 * No-ops silently if `gaEvent` is not a `GaEvent` instance, or if GA
 * is disabled/not yet ready (`Vue.prototype.$gtagReady` resolves to `null`).
 */
export function sendGaEvent(gaEvent: GaEvent) {
  if (!(gaEvent instanceof GaEvent)) {
    return;
  }

  Vue.prototype.$gtagReady.then((ready: any) => {
    if (ready === null) {
      return;
    }

    if (!GaEvent.isSourcePageSet() && router.currentRoute?.path) {
      gaEvent.addSourcePage(router.currentRoute.path);
    }
    gaEvent.addMemberStatus(store.getters.membershipTypeName);

    const { name, params } = gaEvent.toPayload();

    if (AppConfig.get('isLocal')) {
      /* eslint-disable no-console */
      console.group(`\n%c[GA EVENT] - ${name}`, 'padding: 2px 6px; border-radius: 3px; font-weight: bold;');
      console.table(params);
      console.log('\n');
      console.groupEnd();
      /* eslint-enable no-console */
    }

    gtagEvent(name, params);

    if (router.currentRoute?.path) {
      GaEvent.setSourcePage(router.currentRoute.path);
    }
  });
}

export function sendContentItemDetailsViewed(contentType: string, item: any) {
  if (!item || !item.id || !item.slug) {
    return;
  }

  const gaEvent = new ContentItemDetailsViewedGaEvent(item.slug)
    .addContentType(contentType)
    .addContentId(item.id)
    .addContentName(item.title);

  sendGaEvent(gaEvent);
}
